// ============================================================
// AssignProducts.js  –  Fresh Hills Admin
// Tab 1: Assign New Stock (original form)
// Tab 2: Stock Manager — all fridges, edit inline, bulk restock
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import {
  getFridges, getProducts, assignProduct, getFridgeProducts,
  getFridgesStock, setStockQty, bulkRestockFridge,
  resolveImageUrl,
} from '../../services/api';
import toast from 'react-hot-toast';
import AdminLayout from './AdminLayout';
import {
  ClipboardDocumentListIcon, CheckCircleIcon, CubeIcon,
  ArrowPathIcon, PencilSquareIcon,
} from '@heroicons/react/24/outline';

const CATEGORY_EMOJI = {
  Milk: '🥛', Curd: '🥛', Paneer: '🧀', Ghee: '🫙', Butter: '🧈', Buttermilk: '🧃',
};
const getEmoji = (cat) => CATEGORY_EMOJI[cat] || '📦';

const AssignProducts = () => {
  const [tab, setTab] = useState('assign'); // 'assign' | 'stock'

  // ── ASSIGN tab state ───────────────────────────────────────
  const [fridges,        setFridges]        = useState([]);
  const [products,       setProducts]       = useState([]);
  const [selFridge,      setSelFridge]      = useState('');
  const [selProduct,     setSelProduct]     = useState('');
  const [quantity,       setQuantity]       = useState('');
  const [expiryDate,     setExpiryDate]     = useState('');
  const [minStock,       setMinStock]       = useState(5);
  const [fridgeProducts, setFridgeProducts] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [submitting,     setSubmitting]     = useState(false);
  const [loadingInv,     setLoadingInv]     = useState(false);

  // ── STOCK tab state ────────────────────────────────────────
  const [allFridges,   setAllFridges]   = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [expanded,     setExpanded]     = useState({});
  const [edits,        setEdits]        = useState({});
  const [saving,       setSaving]       = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [imgErrors,    setImgErrors]    = useState({});

  // ── Initial load ───────────────────────────────────────────
  useEffect(() => {
    Promise.all([getFridges(), getProducts()])
      .then(([fr, pr]) => {
        setFridges(fr.data.fridges || []);
        setProducts(pr.data.products || []);
      })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  // ── Load inventory when fridge changes (Assign tab) ───────
  const refreshInv = (fid) => {
    if (!fid) { setFridgeProducts([]); return; }
    setLoadingInv(true);
    getFridgeProducts(fid)
      .then(r => setFridgeProducts(r.data.products || []))
      .catch(() => toast.error('Failed to load fridge inventory'))
      .finally(() => setLoadingInv(false));
  };
  useEffect(() => { refreshInv(selFridge); }, [selFridge]);

  // ── Load all fridge stocks (Stock tab) ────────────────────
  const loadAllStocks = useCallback(async () => {
    setStockLoading(true);
    try {
      const r = await getFridgesStock();
      if (r.data.success) {
        setAllFridges(r.data.fridges);
        if (r.data.fridges.length > 0) {
          setExpanded(prev =>
            Object.keys(prev).length === 0 ? { [r.data.fridges[0].id]: true } : prev
          );
        }
      }
    } catch {
      toast.error('Failed to load stock data');
    } finally {
      setStockLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 'stock') loadAllStocks(); }, [tab, loadAllStocks]);

  // ── Assign form submit ─────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selFridge || !selProduct || !quantity) { toast.error('Fill all required fields'); return; }
    setSubmitting(true);
    try {
      const res = await assignProduct({
        fridge_id:       parseInt(selFridge),
        product_id:      parseInt(selProduct),
        quantity:        parseInt(quantity),
        expiry_date:     expiryDate || null,
        min_stock_level: parseInt(minStock),
        min_quantity:    parseInt(minStock),
      });
      if (res.data.success) {
        toast.success('Product assigned successfully!');
        setSelProduct(''); setQuantity(''); setExpiryDate(''); setMinStock(5);
        refreshInv(selFridge);
      } else {
        toast.error(res.data.message || 'Assignment failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign product');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Stock tab helpers ──────────────────────────────────────
  const getQty = (stockId, original) =>
    edits[stockId] !== undefined ? edits[stockId] : original;

  const isDirty = (stockId, original) =>
    edits[stockId] !== undefined && Number(edits[stockId]) !== Number(original);

  const setQty = (stockId, val) =>
    setEdits(prev => ({ ...prev, [stockId]: val === '' ? 0 : Number(val) }));

  const saveOne = async (stockId, productName, originalQty) => {
    const qty = edits[stockId] !== undefined ? Number(edits[stockId]) : Number(originalQty);
    setSaving(prev => ({ ...prev, [stockId]: true }));
    try {
      const r = await setStockQty(stockId, qty);
      if (r.data.success) {
        toast.success(`${productName} → ${qty} units`);
        setEdits(prev => { const n = { ...prev }; delete n[stockId]; return n; });
        setAllFridges(prev => prev.map(f => ({
          ...f,
          products: f.products.map(p =>
            p.stock_id === stockId ? { ...p, quantity: qty } : p
          ),
        })));
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(prev => { const n = { ...prev }; delete n[stockId]; return n; }); }
  };

  const bulkRestock = async (fridge) => {
    const items = fridge.products
      .filter(p => isDirty(p.stock_id, p.quantity))
      .map(p => ({ stock_id: p.stock_id, quantity: Number(edits[p.stock_id]) }));
    if (!items.length) { toast('No changes to save'); return; }
    const k = `bulk_${fridge.id}`;
    setSaving(prev => ({ ...prev, [k]: true }));
    try {
      const r = await bulkRestockFridge(fridge.id, items);
      if (r.data.success) {
        toast.success(`${fridge.apartment} — ${items.length} product(s) restocked`);
        setEdits(prev => {
          const n = { ...prev };
          fridge.products.forEach(p => delete n[p.stock_id]);
          return n;
        });
        await loadAllStocks();
      }
    } catch { toast.error('Bulk restock failed'); }
    finally { setSaving(prev => { const n = { ...prev }; delete n[k]; return n; }); }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const hasDirty     = (fridge) => fridge.products.some(p => isDirty(p.stock_id, p.quantity));

  const visibleFridges = allFridges.filter(f => {
    if (filterStatus === 'low') return f.low_stock_count > 0;
    if (filterStatus === 'out') return f.out_stock_count > 0;
    return true;
  });

  const totalOut = allFridges.reduce((a, f) => a + (f.out_stock_count || 0), 0);
  const totalLow = allFridges.reduce((a, f) => a + (f.low_stock_count || 0), 0);

  const selFridgeObj = fridges.find(f => String(f.id) === String(selFridge));
  const minLvl   = (fp) => Number(fp.min_stock_level || fp.min_quantity || 5);
  const inStock  = fridgeProducts.filter(fp => Number(fp.quantity) > minLvl(fp)).length;
  const lowItems = fridgeProducts.filter(fp => Number(fp.quantity) > 0 && Number(fp.quantity) <= minLvl(fp)).length;
  const outItems = fridgeProducts.filter(fp => Number(fp.quantity) === 0).length;

  /* ── CSS ── */
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Nunito',sans-serif;}
    input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;}
    input[type=number]{-moz-appearance:textfield;}

    /* PAGE HEAD */
    .ap-ph{margin-bottom:1rem;}
    @media(min-width:768px){.ap-ph{margin-bottom:1.5rem;}}
    .ap-title{font-size:1.2rem;font-weight:900;color:#111827;}
    @media(min-width:480px){.ap-title{font-size:1.4rem;}}
    @media(min-width:768px){.ap-title{font-size:1.6rem;}}
    .ap-sub{font-size:0.75rem;color:#64748b;font-weight:600;margin-top:0.1rem;}

    /* TABS */
    .ap-tabs{display:flex;background:#f1f5f9;border-radius:9999px;padding:0.2rem;margin-bottom:1.25rem;width:fit-content;gap:0.1rem;}
    @media(min-width:480px){.ap-tabs{margin-bottom:1.5rem;}}
    .ap-tab{display:flex;align-items:center;gap:0.35rem;padding:0.42rem 0.9rem;border-radius:9999px;border:none;font-weight:700;font-size:0.78rem;cursor:pointer;font-family:'Nunito',sans-serif;color:#64748b;background:none;transition:all 0.2s;white-space:nowrap;}
    @media(min-width:480px){.ap-tab{padding:0.45rem 1.1rem;font-size:0.83rem;}}
    @media(min-width:768px){.ap-tab{padding:0.5rem 1.3rem;font-size:0.88rem;}}
    .ap-tab svg{width:0.85rem;height:0.85rem;}
    .ap-tab.on{background:white;color:#059669;box-shadow:0 2px 8px rgba(0,0,0,0.08);}

    /* GRID */
    .ap-grid{display:grid;grid-template-columns:1fr;gap:0.85rem;}
    @media(min-width:1024px){.ap-grid{grid-template-columns:1fr 1fr;gap:1.5rem;}}

    /* CARD */
    .ap-card{background:white;border-radius:1rem;padding:1rem;border:1px solid #f1f5f9;box-shadow:0 4px 14px rgba(0,0,0,0.04);}
    @media(min-width:480px){.ap-card{border-radius:1.25rem;padding:1.35rem;}}
    @media(min-width:768px){.ap-card{border-radius:1.5rem;padding:1.75rem;}}
    .ap-ct{font-size:0.88rem;font-weight:800;color:#111827;margin-bottom:1rem;display:flex;align-items:center;gap:0.42rem;}
    @media(min-width:768px){.ap-ct{font-size:1rem;margin-bottom:1.5rem;}}
    .ap-ct svg{width:0.95rem;height:0.95rem;color:#059669;flex-shrink:0;}
    @media(min-width:768px){.ap-ct svg{width:1.1rem;height:1.1rem;}}

    /* FORM */
    .ap-fld{margin-bottom:0.85rem;}
    @media(min-width:768px){.ap-fld{margin-bottom:1.15rem;}}
    .ap-lbl{display:block;font-size:0.74rem;font-weight:700;color:#374151;margin-bottom:0.35rem;}
    @media(min-width:768px){.ap-lbl{font-size:0.82rem;margin-bottom:0.45rem;}}
    .ap-inp,.ap-sel{width:100%;padding:0.6rem 0.85rem;border:1.5px solid #e5e7eb;border-radius:0.8rem;font-size:0.86rem;font-family:'Nunito',sans-serif;font-weight:600;color:#111827;background:#f9fafb;outline:none;transition:all 0.2s;}
    @media(min-width:768px){.ap-inp,.ap-sel{padding:0.7rem 1rem;border-radius:0.875rem;}}
    .ap-inp:focus,.ap-sel:focus{border-color:#059669;background:white;box-shadow:0 0 0 3px rgba(5,150,105,0.1);}
    .ap-hint{font-size:0.67rem;color:#9ca3af;margin-top:0.3rem;font-weight:600;}
    .ap-warn{font-size:0.67rem;color:#dc2626;margin-top:0.3rem;font-weight:600;}
    .ap-2col{display:grid;grid-template-columns:1fr 1fr;gap:0.65rem;}
    .ap-sub-btn{width:100%;padding:0.75rem;background:linear-gradient(135deg,#10b981,#059669);color:white;font-weight:800;font-size:0.88rem;border:none;border-radius:9999px;cursor:pointer;transition:all 0.3s;font-family:'Nunito',sans-serif;box-shadow:0 4px 14px rgba(16,185,129,0.3);margin-top:0.4rem;display:flex;align-items:center;justify-content:center;gap:0.42rem;}
    .ap-sub-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 8px 20px rgba(16,185,129,0.35);}
    .ap-sub-btn:disabled{opacity:0.6;cursor:not-allowed;transform:none;}
    .ap-sub-btn svg{width:0.95rem;height:0.95rem;}

    /* INVENTORY */
    .ap-isub{font-size:0.76rem;color:#64748b;font-weight:600;margin-bottom:0.85rem;}
    .ap-chips{display:flex;gap:0.42rem;flex-wrap:wrap;margin-bottom:0.85rem;}
    .ap-chip{display:flex;align-items:center;gap:0.25rem;padding:0.2rem 0.65rem;border-radius:9999px;font-size:0.64rem;font-weight:700;border:1.5px solid;white-space:nowrap;}
    .ap-ilist{display:flex;flex-direction:column;gap:0.55rem;max-height:400px;overflow-y:auto;}
    .ap-iitem{display:flex;align-items:center;justify-content:space-between;padding:0.75rem 0.9rem;background:#f8fafc;border-radius:0.9rem;border:1.5px solid #f1f5f9;transition:all 0.2s;gap:0.5rem;}
    .ap-iitem:hover{border-color:#d1fae5;background:#f0fdf4;}
    .ap-iname{font-weight:800;color:#111827;font-size:0.82rem;margin-bottom:0.12rem;}
    .ap-imeta{font-size:0.66rem;color:#94a3b8;font-weight:600;}
    .ap-ibadge{font-size:0.64rem;font-weight:700;padding:0.18rem 0.6rem;border-radius:9999px;white-space:nowrap;flex-shrink:0;}
    .s-ok{background:#ecfdf5;color:#059669;}
    .s-low{background:#fef3c7;color:#d97706;}
    .s-out{background:#fff1f2;color:#dc2626;}

    /* EMPTY / LOADING */
    .ap-empty{padding:2.5rem 1.25rem;text-align:center;background:#f8fafc;border-radius:0.9rem;color:#94a3b8;}
    .ap-empty svg{width:2.2rem;height:2.2rem;margin:0 auto 0.62rem;opacity:0.35;display:block;}
    .ap-empty p{font-weight:700;font-size:0.9rem;margin-bottom:0.2rem;}
    .ap-empty small{font-size:0.73rem;display:block;}
    .ap-ild{display:flex;align-items:center;justify-content:center;gap:0.45rem;padding:2rem;font-size:0.8rem;font-weight:700;color:#64748b;}
    .ap-isp{width:1.1rem;height:1.1rem;border-radius:50%;border:2px solid #e5e7eb;border-top-color:#059669;animation:apsp 0.7s linear infinite;}
    .ap-bsp{width:0.9rem;height:0.9rem;border:2px solid rgba(255,255,255,0.4);border-top-color:white;border-radius:50%;display:inline-block;animation:apsp 0.7s linear infinite;}
    @keyframes apsp{to{transform:rotate(360deg);}}
    .ap-pgld{display:flex;justify-content:center;align-items:center;height:60vh;}
    .ap-pgsp{width:2.75rem;height:2.75rem;border-radius:50%;border:3px solid #f3f4f6;border-top-color:#059669;animation:apsp 0.7s linear infinite;}

    /* ── STOCK TAB ── */
    .sm-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:0.65rem;margin-bottom:1.1rem;}
    @media(min-width:480px){.sm-summary{gap:0.85rem;margin-bottom:1.35rem;}}
    .sm-scard{background:white;border:1.5px solid #f3f4f6;border-radius:0.9rem;padding:0.85rem;text-align:center;cursor:pointer;transition:all 0.2s;box-shadow:0 2px 8px rgba(0,0,0,0.03);}
    @media(min-width:480px){.sm-scard{border-radius:1.1rem;padding:1rem 0.75rem;}}
    .sm-scard:hover{border-color:#d1fae5;}
    .sm-scard.act{border-color:#059669;background:#f0fdf4;}
    .sm-snum{font-size:1.4rem;font-weight:900;margin-bottom:0.1rem;}
    @media(min-width:480px){.sm-snum{font-size:1.65rem;}}
    .sm-slbl{font-size:0.6rem;font-weight:700;color:#6b7280;}
    @media(min-width:480px){.sm-slbl{font-size:0.68rem;}}

    .sm-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:0.9rem;gap:0.75rem;}
    .sm-reload{display:flex;align-items:center;gap:0.3rem;padding:0.42rem 0.9rem;background:#f0fdf4;color:#059669;border:1.5px solid #d1fae5;border-radius:9999px;font-weight:700;font-size:0.74rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all 0.2s;flex-shrink:0;margin-top:0.1rem;}
    .sm-reload:hover{background:#dcfce7;}
    .sm-reload svg{width:0.8rem;height:0.8rem;}

    /* FRIDGE PANEL */
    .sm-panel{background:white;border:1.5px solid #e5e7eb;border-radius:1.1rem;margin-bottom:0.75rem;overflow:hidden;transition:border-color 0.2s;box-shadow:0 2px 8px rgba(0,0,0,0.03);}
    @media(min-width:480px){.sm-panel{border-radius:1.25rem;}}
    .sm-panel:hover{border-color:#d1fae5;}
    .sm-phdr{display:flex;align-items:center;justify-content:space-between;padding:0.9rem 1rem;cursor:pointer;gap:0.75rem;}
    @media(min-width:480px){.sm-phdr{padding:1rem 1.25rem;}}
    .sm-pico{width:38px;height:38px;border-radius:0.65rem;background:linear-gradient(135deg,#ecfdf5,#d1fae5);border:1px solid #a7f3d0;display:flex;align-items:center;justify-content:center;font-size:1.15rem;flex-shrink:0;}
    @media(min-width:480px){.sm-pico{width:42px;height:42px;}}
    .sm-pname{font-size:0.88rem;font-weight:800;color:#111827;}
    @media(min-width:480px){.sm-pname{font-size:0.95rem;}}
    .sm-psub{font-size:0.68rem;color:#6b7280;margin-top:0.08rem;}
    .sm-pbadges{display:flex;gap:0.32rem;flex-wrap:wrap;}
    .sm-pbadge{padding:0.18rem 0.55rem;border-radius:9999px;font-size:0.62rem;font-weight:700;}
    @media(min-width:480px){.sm-pbadge{font-size:0.66rem;padding:0.2rem 0.65rem;}}
    .sm-chev{font-size:0.7rem;color:#9ca3af;transition:transform 0.25s;flex-shrink:0;margin-left:0.25rem;}

    /* PRODUCT TABLE */
    .sm-tbody{border-top:1px solid #f3f4f6;padding:0.9rem 1rem;overflow-x:auto;}
    @media(min-width:480px){.sm-tbody{padding:1rem 1.25rem;}}
    .sm-table{width:100%;border-collapse:collapse;min-width:500px;}
    .sm-th{font-size:0.63rem;font-weight:800;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;padding:0.45rem 0.65rem;text-align:left;border-bottom:1px solid #f3f4f6;}
    .sm-td{padding:0.65rem 0.65rem;border-bottom:1px solid #f9fafb;vertical-align:middle;}

    .sm-prodinfo{display:flex;align-items:center;gap:0.55rem;}
    .sm-img{width:34px;height:34px;border-radius:0.45rem;object-fit:contain;background:#f9fafb;border:1px solid #f3f4f6;flex-shrink:0;}
    .sm-emoji{width:34px;height:34px;border-radius:0.45rem;background:#f0fdf4;border:1px solid #d1fae5;display:flex;align-items:center;justify-content:center;font-size:1.2rem;flex-shrink:0;}
    .sm-pname2{font-size:0.8rem;font-weight:700;color:#111827;}
    .sm-pcat{font-size:0.63rem;color:#9ca3af;margin-top:0.06rem;}

    /* STATUS PILL */
    .sm-spill{padding:0.18rem 0.6rem;border-radius:9999px;font-size:0.65rem;font-weight:700;}
    .sp-ok{background:#dcfce7;color:#16a34a;}
    .sp-low{background:#fef3c7;color:#d97706;}
    .sp-out{background:#fee2e2;color:#dc2626;}

    /* QTY EDITOR */
    .sm-qrow{display:flex;align-items:center;gap:0.32rem;}
    .sm-qbtn{width:26px;height:26px;border-radius:50%;border:1.5px solid #e5e7eb;background:white;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.88rem;font-weight:700;color:#374151;flex-shrink:0;font-family:'Nunito',sans-serif;transition:all 0.15s;line-height:1;}
    .sm-qbtn:hover{border-color:#059669;color:#059669;background:#f0fdf4;}
    .sm-qinp{width:56px;height:30px;text-align:center;font-size:0.88rem;font-weight:800;color:#111827;border-radius:0.45rem;outline:none;font-family:'Nunito',sans-serif;transition:border-color 0.2s;}
    .sm-qinp.clean{border:1.5px solid #e5e7eb;background:white;}
    .sm-qinp.dirty{border:1.5px solid #059669;background:#f0fdf4;}

    /* SAVE BUTTON */
    .sm-sbtn{padding:0.3rem 0.72rem;border-radius:9999px;font-size:0.68rem;font-weight:800;cursor:pointer;font-family:'Nunito',sans-serif;border:none;white-space:nowrap;transition:all 0.15s;}
    .sm-sbtn.active{background:linear-gradient(135deg,#10b981,#059669);color:white;box-shadow:0 2px 8px rgba(16,185,129,0.25);}
    .sm-sbtn.active:hover{transform:translateY(-1px);}
    .sm-sbtn.inactive{background:#f3f4f6;color:#9ca3af;cursor:not-allowed;}

    /* FOOTER */
    .sm-foot{display:flex;align-items:center;justify-content:space-between;margin-top:0.85rem;padding-top:0.85rem;border-top:1px solid #f3f4f6;flex-wrap:wrap;gap:0.5rem;}
    .sm-fnote{font-size:0.72rem;color:#6b7280;font-weight:600;}
    .sm-fnote.warn{color:#d97706;font-weight:700;}
    .sm-rbtn{display:flex;align-items:center;gap:0.32rem;padding:0.48rem 1.15rem;background:linear-gradient(135deg,#10b981,#059669);color:white;border:none;border-radius:9999px;font-weight:800;font-size:0.8rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all 0.25s;}
    .sm-rbtn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 12px rgba(16,185,129,0.3);}
    .sm-rbtn:disabled{background:#e5e7eb;color:#9ca3af;cursor:not-allowed;transform:none;box-shadow:none;}
    .sm-rbtn svg{width:0.78rem;height:0.78rem;}
    .sm-empty{text-align:center;padding:1.75rem;color:#9ca3af;font-size:0.82rem;font-weight:600;}

    @keyframes spspin{to{transform:rotate(360deg);}}
    .sm-spin{animation:spspin 0.7s linear infinite;}
  `;

  if (loading) return (
    <AdminLayout>
      <style>{css}</style>
      <div className="ap-pgld"><div className="ap-pgsp" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <style>{css}</style>

      {/* Page header */}
      <div className="ap-ph">
        <div className="ap-title">📦 Products & Stock</div>
        <div className="ap-sub">Assign products to fridges and manage stock levels</div>
      </div>

      {/* Tabs */}
      <div className="ap-tabs">
        <button className={`ap-tab${tab === 'assign' ? ' on' : ''}`} onClick={() => setTab('assign')}>
          <ClipboardDocumentListIcon /> Assign Stock
        </button>
        <button className={`ap-tab${tab === 'stock' ? ' on' : ''}`} onClick={() => setTab('stock')}>
          <PencilSquareIcon /> Stock Manager
        </button>
      </div>

      {/* ════════════════ TAB 1: ASSIGN ════════════════ */}
      {tab === 'assign' && (
        <div className="ap-grid">

          {/* Form card */}
          <div className="ap-card">
            <div className="ap-ct"><ClipboardDocumentListIcon /> Assign New Stock</div>
            <form onSubmit={handleSubmit}>

              <div className="ap-fld">
                <label className="ap-lbl">Select Fridge *</label>
                <select className="ap-sel" value={selFridge}
                  onChange={e => setSelFridge(e.target.value)} required>
                  <option value="">Choose a fridge…</option>
                  {fridges.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.fridge_id} — {f.apartment_name || 'No Apartment'}
                      {f.location ? ` (${f.location})` : ''}
                    </option>
                  ))}
                </select>
                {fridges.length === 0 && (
                  <div className="ap-warn">No fridges found. Create a fridge first.</div>
                )}
              </div>

              <div className="ap-fld">
                <label className="ap-lbl">Select Product *</label>
                <select className="ap-sel" value={selProduct}
                  onChange={e => setSelProduct(e.target.value)} required>
                  <option value="">Choose a product…</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} — ₹{p.price}{p.category ? ` (${p.category})` : ''}
                    </option>
                  ))}
                </select>
                {products.length === 0 && (
                  <div className="ap-warn">No products found. Add products first.</div>
                )}
              </div>

              <div className="ap-2col">
                <div className="ap-fld">
                  <label className="ap-lbl">Quantity *</label>
                  <input className="ap-inp" type="number" min="1" placeholder="e.g. 20"
                    value={quantity} onChange={e => setQuantity(e.target.value)} required />
                </div>
                <div className="ap-fld">
                  <label className="ap-lbl">Min Alert Level</label>
                  <input className="ap-inp" type="number" min="1"
                    value={minStock} onChange={e => setMinStock(e.target.value)} />
                </div>
              </div>

              <div className="ap-fld">
                <label className="ap-lbl">Expiry Date</label>
                <input className="ap-inp" type="date"
                  value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
                <div className="ap-hint">Optional — leave blank to use product default</div>
              </div>

              <button type="submit" className="ap-sub-btn"
                disabled={submitting || !fridges.length || !products.length}>
                {submitting
                  ? <><span className="ap-bsp" /> Assigning…</>
                  : <><CheckCircleIcon /> Assign Product</>
                }
              </button>
            </form>
          </div>

          {/* Current stock panel */}
          <div className="ap-card">
            <div className="ap-ct"><CubeIcon /> Current Stock</div>

            {!selFridge ? (
              <div className="ap-empty">
                <ClipboardDocumentListIcon />
                <p>Select a fridge to view stock</p>
                <small>Choose from the dropdown on the left</small>
              </div>
            ) : loadingInv ? (
              <div className="ap-ild"><div className="ap-isp" /> Loading…</div>
            ) : (
              <>
                <div className="ap-isub">
                  ❄️ {selFridgeObj?.apartment_name || 'Unknown'} — {selFridgeObj?.fridge_id}
                  {selFridgeObj?.location && ` · ${selFridgeObj.location}`}
                </div>

                {fridgeProducts.length > 0 && (
                  <div className="ap-chips">
                    <div className="ap-chip" style={{ background:'#ecfdf5',color:'#059669',borderColor:'#a7f3d0' }}>✓ {inStock} OK</div>
                    {lowItems > 0 && <div className="ap-chip" style={{ background:'#fef3c7',color:'#d97706',borderColor:'#fde68a' }}>⚠ {lowItems} Low</div>}
                    {outItems > 0 && <div className="ap-chip" style={{ background:'#fff1f2',color:'#dc2626',borderColor:'#fecdd3' }}>✕ {outItems} Out</div>}
                  </div>
                )}

                {fridgeProducts.length === 0 ? (
                  <div className="ap-empty">
                    <ClipboardDocumentListIcon />
                    <p>No products assigned yet</p>
                    <small>Use the form on the left to assign products</small>
                  </div>
                ) : (
                  <div className="ap-ilist">
                    {fridgeProducts.map(fp => {
                      const min = minLvl(fp);
                      const qty = Number(fp.quantity);
                      const cls = qty === 0 ? 's-out' : qty <= min ? 's-low' : 's-ok';
                      const txt = qty === 0 ? '✕ Out' : qty <= min ? '⚠ Low' : '✓ OK';
                      return (
                        <div key={fp.id || fp.product_id} className="ap-iitem">
                          <div style={{ minWidth: 0 }}>
                            <div className="ap-iname">{fp.name}</div>
                            <div className="ap-imeta">
                              ₹{fp.price} · Stock:&nbsp;
                              <strong style={{ color: qty===0?'#dc2626':qty<=min?'#d97706':'#059669' }}>{qty}</strong>
                              &nbsp;· Min: {min}
                              {fp.expiry_date && ` · Exp: ${new Date(fp.expiry_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'2-digit'})}`}
                            </div>
                          </div>
                          <span className={`ap-ibadge ${cls}`}>{txt}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ════════════════ TAB 2: STOCK MANAGER ════════════════ */}
      {tab === 'stock' && (
        <>
          {/* Summary cards + refresh */}
          <div className="sm-top">
            <div className="sm-summary" style={{ flex: 1 }}>
              {[
                { label: 'All Fridges',  val: allFridges.length, filter: 'all', color: '#059669' },
                { label: 'Low Stock',    val: totalLow,          filter: 'low', color: '#d97706' },
                { label: 'Out of Stock', val: totalOut,          filter: 'out', color: '#dc2626' },
              ].map(c => (
                <div key={c.filter}
                  className={`sm-scard${filterStatus === c.filter ? ' act' : ''}`}
                  onClick={() => setFilterStatus(c.filter)}>
                  <div className="sm-snum" style={{ color: c.color }}>{c.val}</div>
                  <div className="sm-slbl">{c.label}</div>
                </div>
              ))}
            </div>
            <button className="sm-reload" onClick={loadAllStocks} disabled={stockLoading}>
              <ArrowPathIcon className={stockLoading ? 'sm-spin' : ''} />
              {stockLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {stockLoading && allFridges.length === 0 ? (
            <div className="ap-pgld"><div className="ap-pgsp" /></div>

          ) : visibleFridges.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:'#9ca3af', fontWeight:600 }}>
              No fridges match this filter.
            </div>

          ) : visibleFridges.map(fridge => {
            const isOpen  = !!expanded[fridge.id];
            const dirty   = hasDirty(fridge);
            const bulkKey = `bulk_${fridge.id}`;

            return (
              <div key={fridge.id} className="sm-panel">

                {/* Panel header */}
                <div className="sm-phdr" onClick={() => toggleExpand(fridge.id)}>
                  <div style={{ display:'flex', alignItems:'center', gap:'.65rem', minWidth:0, flex:1 }}>
                    <div className="sm-pico">🧊</div>
                    <div>
                      <div className="sm-pname">{fridge.apartment || 'Unknown Apartment'}</div>
                      <div className="sm-psub">{fridge.location} · {fridge.products.length} product{fridge.products.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'.5rem', flexShrink:0 }}>
                    <div className="sm-pbadges">
                      {fridge.out_stock_count > 0 && <span className="sm-pbadge sp-out">{fridge.out_stock_count} Empty</span>}
                      {fridge.low_stock_count > 0 && <span className="sm-pbadge sp-low">{fridge.low_stock_count} Low</span>}
                      <span className="sm-pbadge sp-ok">{fridge.total_stock} units</span>
                    </div>
                    <span className="sm-chev" style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
                  </div>
                </div>

                {/* Panel body */}
                {isOpen && (
                  <div className="sm-tbody">
                    {fridge.products.length === 0 ? (
                      <div className="sm-empty">No products assigned to this fridge yet.</div>
                    ) : (
                      <>
                        <table className="sm-table">
                          <thead>
                            <tr>
                              <th className="sm-th">Product</th>
                              <th className="sm-th">Price</th>
                              <th className="sm-th">Status</th>
                              <th className="sm-th">Stock Qty</th>
                              <th className="sm-th">Save</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fridge.products.map(product => {
                              const sid      = product.stock_id;
                              const curQty   = getQty(sid, product.quantity);
                              const rowDirty = isDirty(sid, product.quantity);
                              const isSav    = !!saving[sid];
                              const imgSrc   = resolveImageUrl(product.image_url);
                              const n        = Number(curQty);
                              const min      = Number(product.min_quantity || 5);
                              const spCls    = n === 0 ? 'sp-out' : n <= min ? 'sp-low' : 'sp-ok';
                              const spTxt    = n === 0 ? 'Out of Stock' : n <= min ? 'Low Stock' : 'In Stock';

                              return (
                                <tr key={sid}>
                                  <td className="sm-td">
                                    <div className="sm-prodinfo">
                                      {imgSrc && !imgErrors[sid]
                                        ? <img className="sm-img" src={imgSrc} alt={product.name}
                                            onError={() => setImgErrors(p => ({ ...p, [sid]: true }))} />
                                        : <div className="sm-emoji">{getEmoji(product.category)}</div>
                                      }
                                      <div>
                                        <div className="sm-pname2">{product.name}</div>
                                        <div className="sm-pcat">{product.category}</div>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="sm-td">
                                    <span style={{ fontWeight:700, color:'#111827', fontSize:'.82rem' }}>
                                      ₹{Number(product.price).toFixed(2)}
                                    </span>
                                  </td>

                                  <td className="sm-td">
                                    <span className={`sm-spill ${spCls}`}>{spTxt}</span>
                                  </td>

                                  <td className="sm-td">
                                    <div className="sm-qrow">
                                      <button className="sm-qbtn"
                                        onClick={() => setQty(sid, Math.max(0, Number(curQty) - 1))}>−</button>
                                      <input type="number" min="0"
                                        className={`sm-qinp ${rowDirty ? 'dirty' : 'clean'}`}
                                        value={curQty}
                                        onChange={e => setQty(sid, e.target.value)} />
                                      <button className="sm-qbtn"
                                        onClick={() => setQty(sid, Number(curQty) + 1)}>+</button>
                                    </div>
                                  </td>

                                  <td className="sm-td">
                                    <button
                                      className={`sm-sbtn ${rowDirty && !isSav ? 'active' : 'inactive'}`}
                                      disabled={!rowDirty || isSav}
                                      onClick={() => saveOne(sid, product.name, product.quantity)}>
                                      {isSav ? '…' : rowDirty ? '💾 Save' : 'Saved'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* Bulk restock footer */}
                        <div className="sm-foot">
                          <div className={`sm-fnote${dirty ? ' warn' : ''}`}>
                            {dirty ? '⚠ Unsaved changes — use Restock All to save at once' : '✓ All changes saved'}
                          </div>
                          <button
                            className="sm-rbtn"
                            disabled={!dirty || !!saving[bulkKey]}
                            onClick={() => bulkRestock(fridge)}>
                            <ArrowPathIcon className={saving[bulkKey] ? 'sm-spin' : ''} />
                            {saving[bulkKey] ? 'Saving…' : '🔄 Restock All'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

    </AdminLayout>
  );
};

export default AssignProducts;